/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


 function initProductFAQ(index) {
    let allQuestions = document.querySelectorAll('.product-faq-item');
    let currentQuestion = document.querySelector(`.product-faq-item[data-index="${index}"]`);

    allQuestions.forEach(question => {
        if (question !== currentQuestion) {
            question.classList.remove('active');
        } else {
            if (question.classList.contains('active')) {
                question.classList.remove('active');
            } else {
                question.classList.add('active');
            }
        }
    });
}

function initCustomInstaFeedCarousel() {
    let wrapper = document.querySelector('.insta-feed-wrapper');
    let carousel = document.querySelector('.insta-feed-carousel');
    let carouselInner = carousel.querySelector('.insta-feed-inner');
    let carouselItems = carousel.querySelectorAll('.insta-post-item');

    let carouselWidth = carousel.offsetWidth;
    let itemWidth = carouselItems[0].offsetWidth;
    let itemsPerScreen = Math.floor(carouselWidth / itemWidth);
    let maxWidth = carouselInner.offsetWidth;

    let maxIndex = carouselItems.length - itemsPerScreen;
    let currentIndex = 0;
    let newIndex;

    carousel.addEventListener('scroll', () => {
        if(carousel.classList.contains('scrolling')) {
            return;
        }
        carouselItems.forEach((item, index) => {
            if (item.classList.contains('current')) {
                currentIndex = index;
                item.classList.remove('current');
            }
        });

        newIndex = Math.round(carousel.scrollLeft / itemWidth);
        if (newIndex < 0) {
            newIndex = 0;
        } else if (newIndex > maxIndex) {
            newIndex = maxIndex;
        }
        carouselItems[newIndex].classList.add('current');
    });   
    
    checkInstaFeedCarouselWidth();

    window.addEventListener('resize', () => {
        checkInstaFeedCarouselWidth();
    });
}

function checkInstaFeedCarouselWidth() {
    let wrapper = document.querySelector('.insta-feed-wrapper');
    let carouselInner = wrapper.querySelector('.insta-feed-inner');

    let maxWidth = carouselInner.offsetWidth;
    let windowWidth = window.innerWidth;

    if(windowWidth >= maxWidth) {
        wrapper.classList.add('max-width');
    } else {
        wrapper.classList.remove('max-width');
    }
    
}

function scrollCarousel(direction) {
    let carousel = document.querySelector('.insta-feed-carousel');
    carousel.classList.add('scrolling');
    let carouselItems = carousel.querySelectorAll('.insta-post-item');

    let carouselWidth = carousel.offsetWidth;
    let itemWidth = carouselItems[0].offsetWidth;
    let itemsPerScreen = Math.floor(carouselWidth / itemWidth);

    let maxIndex = carouselItems.length - itemsPerScreen;
    let currentIndex = 0;
    let newIndex;

    carouselItems.forEach((item, index) => {
        if (item.classList.contains('current')) {
            currentIndex = index;
            item.classList.remove('current');
        }
    });

    if(direction === 'left') {
        newIndex = currentIndex === 0 ? maxIndex : currentIndex - 1;
    } else {
        newIndex = currentIndex === maxIndex ? 0 : currentIndex + 1;
    }

    carouselItems[newIndex].classList.add('current');
    // remove scroll listener        
    carouselItems[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    setTimeout(() => {
        carousel.classList.remove('scrolling');
    }, 500);
}

function initAltAddToCart() {
    let productForm = document.querySelector('.js-product-form');
    let variantId = productForm.querySelector('input[name="id"]');
    let altAddToCartBtns = document.querySelectorAll('.alt-add-to-cart-wrapper');
    let soldOut = productForm.querySelector('.add-to-cart').getAttribute('disabled') ? true : false;

    if (soldOut) {
        altAddToCartBtns.forEach(btn => {
            btn.classList.add('disabled');
        });
    }

    variantId.addEventListener('change', () => {

        if(altAddToCartBtns) {
            if (soldOut) {
                altAddToCartBtns.forEach(btn => {
                    btn.classList.add('disabled');
                });
            } else {
                altAddToCartBtns.forEach(btn => {
                    btn.classList.remove('disabled');
                });
            }
        }
    });
}

function toggleProductVideo(videoId) {
    let videoWrapper = document.querySelector(`.video-${videoId}`);
    let btn = videoWrapper.querySelector('.product-video-btn');
    let video = videoWrapper.querySelector('video');

    if (video.paused) {
        video.play();
        btn.dataset.action = 'pause';
        btn.setAttribute('aria-label', 'Pause Video');
    } else {
        video.pause();
        btn.dataset.action = 'play';
        btn.setAttribute('aria-label', 'Play Video');
    }

}

/* ANCHOR PRE-ORDER DISCLAIMER */
function preOrderDisclaimer(status) {
    let addToCartBtn = document.querySelector('.add-to-cart');
    let preOrderInputs = document.querySelector('.js-pre-order-inputs');
    let inputs = preOrderInputs.querySelectorAll('input');

  
    if(status == 'approved') {
        addToCartBtn.disabled = false;
        inputs[0].checked = true;
        inputs[1].checked = false;
    } else {
        addToCartBtn.disabled = true;
        inputs[0].checked = false;
        inputs[1].checked = true;
    }
}

function setPreOrderStatusBundle(hasPreOrderDate) {
    const preOrderWrapper = document.querySelector('.pre-order-disclaimer');
    const preOrderInputs = document.querySelector('.js-pre-order-inputs');
    const addToCartBtn = document.querySelector('.quantity-submit-row__submit .btn');
    const btnText = addToCartBtn.querySelector('.add-to-cart-text');
    const inputs = preOrderInputs ? preOrderInputs.querySelectorAll('input') : null;
    const preOrderProperty = document.querySelector('.js-pre-order-property');


    if(btnText.textContent === 'Sold Out') {
      addToCartBtn.disabled = true;
      if (hasPreOrderDate) {
        preOrderWrapper.classList.add('sold-out')
        preOrderWrapper.classList.remove('hidden');
      } else {
        preOrderWrapper.classList.add('hidden');
      }
    } else if(hasPreOrderDate) {
        addToCartBtn.disabled = true;
        inputs[0].checked = false;
        inputs[1].checked = true;
        preOrderWrapper.classList.remove('hidden');
        preOrderWrapper.classList.remove('sold-out');
        preOrderProperty.value = hasPreOrderDate;
        preOrderProperty.checked = true;
    } else {
      addToCartBtn.disabled = false;
      if(preOrderWrapper) {
        preOrderWrapper.classList.add('hidden');
      }
      preOrderProperty.value = '';
      preOrderProperty.checked = false;
    }
}

/* ANCHOR Bundle Products */
function openProductTab(productHandle) {
    let productTabs = document.querySelectorAll('.bundle-products-item');
    let productTabLinks = document.querySelectorAll('.bundle-products-wrapper-nav-list-item');

    productTabs.forEach(tab => {
        tab.classList.remove('active');
    });

    productTabLinks.forEach(link => {
        link.classList.remove('active');
    });

    let activeTab = document.querySelector(`.bundle-products-item[data-product="${productHandle}"]`);
    let activeTabLink = document.querySelector(`.bundle-products-wrapper-nav-list-item[data-product="${productHandle}"]`);

    activeTab.classList.add('active');
    activeTabLink.classList.add('active');

    activeTab.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });

}

function initBundleVariantPicker() {
    let url = new URL(window.location.href);
    let variantId = url.searchParams.get('variant');
    let optionWrappers = document.querySelectorAll('.option-selector');
    let variants = document.querySelectorAll('.js-bundle-variant');

    if (variantId) {
        variants.forEach(variant => {
            if(variant.value == variantId) {
                variant.classList.add('selected');
                let variantOptions = JSON.parse(variant.dataset.options);
                optionWrappers.forEach((optionWrapper, index) => {
                    let options = optionWrapper.querySelectorAll('.swatch');
                    options.forEach(option => {
                        if(option.dataset.name === variantOptions[index]) {
                            updateBundleVariant(option, optionWrapper);
                        } 
                    })
                })
            } else {
                variant.classList.remove('selected');
            }
        })
    }

    let selectedOptions = [];
    let selectedVariant = document.querySelector('.js-bundle-variant.selected');
    if(!selectedVariant) {
        selectedVariant = variants[0];
        selectedVariant.classList.add('selected');
    };

    optionWrappers.forEach(optionWrapper => {
        let options = optionWrapper.querySelectorAll('.swatch');
        let selected = optionWrapper.querySelector('.selected');
        let product = selected.dataset.product;
        selectedOptions.push({name: selected.dataset.name, product: product});
        options.forEach(option => {
            option.addEventListener('click', () => {
                updateBundleVariant(option, optionWrapper);
            });
        });
    });

    updateBundleGallery(selectedOptions);
    updateBundlePrice(selectedVariant.value);
    updateBundleUrl(selectedVariant.value);
}

function updateBundleVariant(selectedOption, selectedOptionWrapper) {
    let optionWrappers = document.querySelectorAll('.option-selector');
    let selectedOptions = [];
    let variantNameEl = selectedOptionWrapper.querySelector('.option-selector__label-value');

    optionWrappers.forEach(optionWrapper => {
        let options = optionWrapper.querySelectorAll('.swatch');

        options.forEach(option => {
            let product = option.dataset.product;
            if(selectedOptionWrapper === optionWrapper) {
                if(option === selectedOption) {
                    option.classList.add('selected');
                    selectedOptions.push({name: option.dataset.name, product:  product});
                    variantNameEl.textContent = option.dataset.name;
                } else {
                    option.classList.remove('selected');
                }
            } else {
                if(option.classList.contains('selected')) {
                    selectedOptions.push({name: option.dataset.name, product:  product});
                }
            }
        });
    });

    let variantInputs = document.querySelectorAll('.js-bundle-variant');
    let variantId = 0;
    let variantSoldOut = false;
    let hasPreOrderDate = false;

    variantInputs.forEach(input => {

        let variantOptions = JSON.parse(input.dataset.options);
        let numberOfOptions = selectedOptions.length;
        let match = 0;

        for (let i = 0; i < numberOfOptions; i++) {
            if (selectedOptions[i].name === variantOptions[i]) {
                match++;
            }
        }
        if (match === numberOfOptions) {
            variantId = input.value;
            input.classList.add('selected');
            variantSoldOut = input.dataset.available === 'false' ? true : false;
            hasPreOrderDate = input.dataset.preOrder != 'false' ? input.dataset.preOrder : false;
        } else {
            input.classList.remove('selected');
        }

    });

    updateBundleGallery(selectedOptions);

    let productForm = document.querySelector('.js-product-form');
    let variantIdInput = productForm.querySelector('input[name="id"]');
    variantIdInput.value = variantId;

    let addToCartBtn = productForm.querySelector('.add-to-cart');
    let addToCartBtnText = addToCartBtn.querySelector('.add-to-cart-text');

    if (variantSoldOut) {
        addToCartBtn.disabled = true;
        addToCartBtnText.textContent = 'Sold Out';
    } else {
        addToCartBtn.disabled = false;
        addToCartBtnText.textContent = 'Add to Cart';
    }

    updateBundlePrice(variantId);
    setPreOrderStatusBundle(hasPreOrderDate);
    updateBundleUrl(variantId);
    
}

function updateBundlePrice(variantId) {
    let variantInput = document.querySelector(`.js-bundle-variant[value="${variantId}"]`);
    let priceElements = document.querySelectorAll('.js-bundle-price');
    let comparePriceElements = document.querySelectorAll('.js-bundle-compare');
    let price = variantInput.dataset.price;
    let comparePrice = variantInput.dataset.compareAtPrice;

    priceElements.forEach(el => {
        el.textContent = price;
    });

    comparePriceElements.forEach(el => {
        el.textContent = comparePrice;
    });
}

function updateBundleGallery(selectedOptions) {
    let productMediaItems = document.querySelectorAll('.product-media-collage__item');
    let totalOptions = selectedOptions.length;

    productMediaItems.forEach(item => {
        let alt = item.querySelector('img').getAttribute('alt');

        if (alt.includes('All')) {
            item.classList.remove('product-media-collage__item--inactive-option');
        } else {
            for (let i = 0; i < totalOptions; i++) {
                let altText = selectedOptions[i].name + ' / ' + selectedOptions[i].product;

                let altVariant = alt.split('@')[0];
                if (altVariant == altText) {
                    item.classList.remove('product-media-collage__item--inactive-option');
                    break;
                } else {
                    item.classList.add('product-media-collage__item--inactive-option');
                }
            }
        }
    });
}

function updateBundleUrl(variantId) {
    let url = new URL(window.location.href);
    let productHandle = document.querySelector('.js-product-form').dataset.productHandle;
    if(productHandle && url.pathname.includes(productHandle)) {
        url.searchParams.set('variant', variantId);
        window.history.pushState({}, '', url);
    }
}

/* ALT ADD TO CART */
function altAddToCart() {
    let productForm = document.querySelector('.js-product-form');
    let variantId = productForm.querySelector('input[name="id"]');
    let quantity = productForm.querySelector('input[name="quantity"]');

    let formData = {
        'items': [{
            'id': variantId.value,
            'quantity': quantity.value
        }]
    };
       
    fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
    .then(response => {
        return response.json();
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

function changeCTAVariantLabel() {
    let productForm = document.querySelector('.js-product-form');
    if(productForm && productForm.classList.contains('quickbuy-content')) {
        return;
    }

    let stickySwatch = document.querySelector('.sticky-add-to-cart-swatch');
    let swatchName = document.querySelectorAll('.sticky-add-to-cart-name--option');
    let inputs = document.querySelectorAll('.opt-btn');
    let selectedVariant;

    inputs.forEach(input => {
        if (input.checked) {
            selectedVariant = input;
            if(swatchName.length === 1) {                    
                if (selectedVariant.getAttribute('data-option-type') === 'Color') {
                    let swatch = selectedVariant.value.toLowerCase();
                    stickySwatch.setAttribute('data-swatch', swatch);
                }
                swatchName[0].innerText = selectedVariant.value;
            } else {
                if (selectedVariant.getAttribute('data-option-type') === 'Color') {
                    swatchName[0].innerText = selectedVariant.value;
                    let swatch = selectedVariant.value.toLowerCase();
                    stickySwatch.setAttribute('data-swatch', swatch);
                } else {
                    swatchName[1].innerText = selectedVariant.value;
                }
            }
        }
    });
}

function watchForScrollPosition() {
    let stickyCart = document.querySelector('.sticky-cart-cta--wrapper ');
    let topSection = document.querySelector('product-form');
    let topSectionHeight = topSection.offsetHeight + 300;

    window.addEventListener('scroll', () => {
        let scrollPosition = window.scrollY;
        
        if (scrollPosition > topSectionHeight) {
            stickyCart.classList.add('active');
        } else {
            stickyCart.classList.remove('active');
        }
    });

    window.addEventListener('resize', () => {
        topSectionHeight = topSection.offsetHeight + 300;
    });
};

function bundleCardSwatchImages() {
    let bundleCards = document.querySelectorAll('.bundle-card');

    if(bundleCards.length > 0) {
        bundleCards.forEach(card => {
            let swatches = card.querySelectorAll('.product-block-options__item');
            let images = card.querySelectorAll('.product-block__image--secondary');
            let primaryImage = card.querySelector('.product-block__image--primary');
        
            swatches.forEach(swatch => {
                const mouseLeaveHandler = removeActiveBundleImg.bind(null, images, primaryImage);

                swatch.addEventListener('mouseenter', () => {
                    addActiveBundleImg(images, primaryImage, swatch);
                    swatch.addEventListener('mouseleave', mouseLeaveHandler);
                })
                swatch.addEventListener('click', () => {
                    addActiveBundleImg(images, primaryImage, swatch);
                    swatch.removeEventListener('mouseleave', mouseLeaveHandler);
                });
            });

        });
    }
}

function removeActiveBundleImg(images, primaryImage) {
    images.forEach(image => {
        image.classList.remove('product-block__image--active');
    });
    primaryImage.classList.add('product-block__image--active');
}

function addActiveBundleImg(images, primaryImage, swatch) {
    let swatchColor = swatch.dataset.optionItem;
    let swatchProduct = swatch.dataset.optionProduct;
    let matchFound = false;
    images.forEach(imageWrapper => {
        let altText = imageWrapper.dataset.alt;
        if(altText.includes(swatchColor) && altText.includes(swatchProduct) && !matchFound) {
            imageWrapper.classList.add('product-block__image--active');
            matchFound = true;
        } else {
            imageWrapper.classList.remove('product-block__image--active');
        }
    });
    primaryImage.classList.remove('product-block__image--active');
}





// Helper function to fetch cart data
function fetchCartData() {
  return fetch('/cart.js')
    .then(res => res.json())
    .catch(err => {
      console.error('Failed to fetch cart data:', err);
      return null;
    });
}

function updateCartCount() {
  fetchCartData().then(cart => {
    const countEls = document.querySelectorAll('.cart-link__count');
    countEls.forEach(el => {
      if (cart && cart.item_count > 0) {
        el.textContent = cart.item_count;
        el.classList.remove('visually-hidden');
      } else {
        el.textContent = '';
        el.classList.add('visually-hidden');
      }
    });
  });
}


// Listen to Rebuy cart events
document.addEventListener('rebuy:cart.change', updateCartCount);
document.addEventListener('rebuy:cart.add', updateCartCount);

function updateRebuyCartCount() {
    fetchCartData()
        .then(cart => {
            const rebuyCount = document.querySelector('.rebuy-cart__item-count.gathre-header-c');
            if (rebuyCount) {
                let cartCount = cart && cart.item_count ? cart.item_count : 0;
                if (cartCount > 0) {
                    rebuyCount.textContent = `(${cartCount})`;
                    rebuyCount.classList.remove('visually-hidden');
                } else {
                    rebuyCount.classList.add('visually-hidden');
                }
            }
        })
        .catch(error => {
            console.error('Failed to update Rebuy cart count:', error);
            const rebuyCount = document.querySelector('.rebuy-cart__item-count.gathre-header-c');
            if (rebuyCount) {
                rebuyCount.classList.add('visually-hidden');
            }
        });
}


// Run on page load
document.addEventListener('DOMContentLoaded', updateRebuyCartCount);

// Sync whenever the header count updates (cart changes)
document.addEventListener('rebuy:cart.change', updateRebuyCartCount);
document.addEventListener('rebuy:cart.add', updateRebuyCartCount);
